import React, { useState, useMemo } from "react";
import Spinner from "../common/Spinner";
import roscasordersService from '../../services/roscaorders.service';
import { useRoscaOrders } from '../../hooks';
import { getDateFormats, getDisplayLabel, getRoscaSeasonYear } from '../../helpers/roscas';
import { formatPhoneForDisplay } from '../../helpers/phone';
import Modal from "../common/Modal";
import RoscaOrder from "./RoscaOrder";
import './RoscaOrders.css';

const ROSCA_DAYS = getDateFormats();
const ROSCA_YEAR = getRoscaSeasonYear();

function getHourGroup(time) {
    if (!time) return "12am";
    // Times are in "9am", "10am", "12pm" format — return as-is for grouping
    return time.toLowerCase().trim();
}

function groupOrdersByHour(orders) {
    const groups = {};
    orders.forEach(order => {
        const hour = getHourGroup(order.time);
        if (!groups[hour]) {
            groups[hour] = [];
        }
        groups[hour].push(order);
    });
    return groups;
}

function sortOrdersForDisplay(orders) {
    const unpicked = orders.filter(o => !o.pickedUp).sort((a, b) => a.time.localeCompare(b.time));
    const picked = orders.filter(o => o.pickedUp).sort((a, b) => a.time.localeCompare(b.time));
    return [...unpicked, ...picked];
}

export default function RoscaOrders() {
    const { orders: rawOrders, loading, setOrders } = useRoscaOrders();
    const [selectedDay, setSelectedDay] = useState(ROSCA_DAYS[0]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [collapsedHours, setCollapsedHours] = useState({});

    const orders = useMemo(() => {
        return rawOrders
            .filter(order => ROSCA_DAYS.includes(order.date))
            .sort((a, b) => a.time.localeCompare(b.time));
    }, [rawOrders]);

    const handleOrderUpdate = (updatedOrder) => {
        if (updatedOrder === null) {
            setOrders(prev => prev.filter(o => o.id !== selectedOrder?.id));
        } else {
            setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
        }
    };

    const handleTogglePickedUp = async (order, e) => {
        e?.stopPropagation();
        try {
            await roscasordersService.togglePickedUp(order.id, order.pickedUp);
            setOrders(prev => prev.map(o =>
                o.id === order.id ? { ...o, pickedUp: !o.pickedUp } : o
            ));
        } catch (error) {
            console.error('Error toggling picked up:', error);
            alert('Error al actualizar');
        }
    };

    const handleRowClick = (order) => {
        setSelectedOrder(order);
    };

    const closeModal = () => {
        setSelectedOrder(null);
    };

    const toggleHourCollapse = (hour) => {
        setCollapsedHours(prev => ({
            ...prev,
            [hour]: !prev[hour]
        }));
    };

    const dayOrders = orders.filter(order => {
        const matchesDay = order.date === selectedDay;
        const matchesSearch = searchQuery === '' ||
            order.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.phoneNumber?.includes(searchQuery);
        return matchesDay && matchesSearch;
    });

    const sortedDayOrders = sortOrdersForDisplay(dayOrders);
    const unpickedOrders = sortedDayOrders.filter(o => !o.pickedUp);
    const pickedUpOrders = sortedDayOrders.filter(o => o.pickedUp);
    const hourGroups = groupOrdersByHour(unpickedOrders);
    const sortedHours = Object.keys(hourGroups).sort();

    const dayTotal = dayOrders.reduce((sum, o) => sum + parseInt(o.amount || 0, 10), 0);
    const dayPaid = dayOrders.filter(o => o.paid === 'Si').length;
    const dayPickedUp = dayOrders.filter(o => o.pickedUp).length;
    const allTotal = orders.reduce((sum, o) => sum + parseInt(o.amount || 0, 10), 0);

    if (loading) {
        return (
            <div className="rosca-loading">
                <Spinner />
            </div>
        );
    }

    const renderOrderRow = (order) => (
        <tr
            key={order.id}
            className={`${order.paid === 'Si' ? 'row-paid' : 'row-unpaid'} ${order.pickedUp ? 'row-picked-up' : ''}`}
            onClick={() => handleRowClick(order)}
            style={{ cursor: 'pointer' }}
        >
            <td className="cell-time">{order.time}</td>
            <td className="cell-name">{order.firstName} {order.lastName}</td>
            <td className="cell-phone">{formatPhoneForDisplay(order.phoneNumber)}</td>
            <td className="cell-amount">{order.amount}</td>
            <td className="cell-paid">
                <span className={order.paid === 'Si' ? 'badge-paid' : 'badge-unpaid'}>
                    {order.paid === 'Si' ? 'SÍ' : 'NO'}
                </span>
            </td>
            <td className="cell-pickup" onClick={(e) => e.stopPropagation()}>
                <button
                    className={`btn-pickup ${order.pickedUp ? 'picked-up' : ''}`}
                    onClick={(e) => handleTogglePickedUp(order, e)}
                    title={order.pickedUp ? 'Marcar como no recogida' : 'Marcar como recogida'}
                >
                    {order.pickedUp ? '✓' : '○'}
                </button>
            </td>
            <td className="cell-cashier">{order.cashier}</td>
        </tr>
    );

    return (
        <div className="rosca-table-container">
            <div className="rosca-table-header">
                <h1 className="rosca-table-title">Roscas {ROSCA_YEAR}</h1>

                <div className="rosca-day-tabs">
                    {ROSCA_DAYS.map(day => {
                        const count = orders.filter(o => o.date === day).reduce((sum, o) => sum + parseInt(o.amount || 0, 10), 0);
                        const shortName = getDisplayLabel(day);
                        return (
                            <button
                                key={day}
                                className={`rosca-day-tab ${selectedDay === day ? 'active' : ''}`}
                                onClick={() => { setSelectedDay(day); setSearchQuery(''); setCollapsedHours({}); }}
                            >
                                <span className="tab-day">{shortName}</span>
                                <span className="tab-count">{count}</span>
                            </button>
                        );
                    })}
                </div>

                <div className="rosca-day-stats">
                    <div className="stat-item">
                        <span className="stat-num">{dayOrders.length}</span>
                        <span className="stat-label">órdenes</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-num">{dayTotal}</span>
                        <span className="stat-label">roscas</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-num stat-green">{dayPaid}</span>
                        <span className="stat-label">pagadas</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-num stat-blue">{dayPickedUp}</span>
                        <span className="stat-label">recogidas</span>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-item">
                        <span className="stat-num stat-gold">{allTotal}</span>
                        <span className="stat-label">total roscas</span>
                    </div>
                </div>

                <div className="rosca-search-row">
                    <input
                        type="text"
                        placeholder="Buscar nombre o teléfono..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="rosca-search"
                    />
                </div>
            </div>

            {dayOrders.length === 0 ? (
                <div className="rosca-empty">
                    <p>No hay órdenes para este día</p>
                </div>
            ) : (
                <div className="rosca-table-wrapper">
                    <table className="rosca-table">
                        <thead>
                            <tr>
                                <th>Hora</th>
                                <th>Nombre</th>
                                <th>Teléfono</th>
                                <th>Cant.</th>
                                <th>Pagado</th>
                                <th>Recogida</th>
                                <th>Cajero</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedHours.map(hour => {
                                const groupOrders = hourGroups[hour];
                                const isCollapsed = collapsedHours[hour];
                                const groupTotal = groupOrders.reduce((sum, o) => sum + parseInt(o.amount || 0, 10), 0);

                                return (
                                    <React.Fragment key={hour}>
                                        <tr
                                            className="hour-group-header"
                                            onClick={() => toggleHourCollapse(hour)}
                                        >
                                            <td colSpan="8">
                                                <span className="hour-group-toggle">
                                                    {isCollapsed ? '▶' : '▼'}
                                                </span>
                                                <span className="hour-group-time">{hour}</span>
                                                <span className="hour-group-count">
                                                    {groupOrders.length} órdenes · {groupTotal} roscas
                                                </span>
                                            </td>
                                        </tr>
                                        {!isCollapsed && groupOrders.map(order => renderOrderRow(order))}
                                    </React.Fragment>
                                );
                            })}

                            {pickedUpOrders.length > 0 && (
                                <>
                                    <tr
                                        className="hour-group-header picked-up-section"
                                        onClick={() => toggleHourCollapse('_pickedUp')}
                                    >
                                        <td colSpan="8">
                                            <span className="hour-group-toggle">
                                                {collapsedHours['_pickedUp'] ? '▶' : '▼'}
                                            </span>
                                            <span className="hour-group-time">Recogidas</span>
                                            <span className="hour-group-count">
                                                {pickedUpOrders.length} órdenes
                                            </span>
                                        </td>
                                    </tr>
                                    {!collapsedHours['_pickedUp'] && pickedUpOrders.map(order => renderOrderRow(order))}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            {selectedOrder && (
                <Modal onClose={closeModal}>
                    <RoscaOrder
                        rosca={selectedOrder}
                        onUpdate={handleOrderUpdate}
                        onClose={closeModal}
                    />
                </Modal>
            )}
        </div>
    );
}
